/** Browser view model: authorized scope plus native display shapes, never analytical policy. */
import * as z from 'zod'
import { insightAnswerKeys, type InsightsReadiness } from '@testfeed/shopify-contracts'
import type { InsightsAcceptedAttempt } from './insightsAttempt'
import { InsightsFailure, throwRenderIssue, type RenderIssue } from './insightsFailure'
import { parseRenderPage, type RenderPage } from './insightRenderPresentation'
const text = z.string().min(1).max(256)
const contextSchema = z.object({ shop: text, orgId: text, connectionId: text, revision: text })
type RenderContext = z.infer<typeof contextSchema>
export type RenderAnswer = { state: string }
export type InsightsRenderReady = {
  state: 'ready'
  context: RenderContext
  answers: Record<string, RenderAnswer>
  composition: { page: RenderPage }
  renderIssues: RenderIssue[]
}
export type InsightsRenderResponse = (InsightsRenderReady | { state: 'unavailable' } | InsightsReadiness) & {
  diagnosticAttempt?: InsightsAcceptedAttempt
}
const envelope = z.object({
  state: z.enum(['ready', 'unavailable']),
  context: contextSchema.optional(),
  answers: z.record(z.string(), z.unknown()).optional(),
  composition: z.unknown(),
})
function scopedContext(raw: unknown, expected: RenderContext, path: string) {
  const parsed = contextSchema.safeParse(raw)
  if (!parsed.success) throw new InsightsFailure('scope', 'invalid_context', path)
  if (Object.entries(expected).some(([key, value]) => parsed.data[key as keyof RenderContext] !== value))
    throw new InsightsFailure('scope', 'context_mismatch', path)
}
function metadataScope(raw: unknown, expected: RenderContext, path: string, requireEvidence = false) {
  const object = z
    .object({
      state: z.string().optional(),
      context: z.unknown().optional(),
      evidence: z.object({ context: z.unknown() }).optional(),
    })
    .safeParse(raw)
  if (!object.success) throw new InsightsFailure('scope', 'invalid_metadata', path)
  if (requireEvidence && object.data.state === 'ready' && !object.data.evidence)
    throw new InsightsFailure('scope', 'missing_evidence_context', path)
  if (object.data.context !== undefined) scopedContext(object.data.context, expected, path)
  if (object.data.evidence) scopedContext(object.data.evidence.context, expected, path)
}
function answer(raw: unknown, path: string): RenderAnswer {
  const parsed = z.object({ state: text }).safeParse(raw)
  if (!parsed.success) throwRenderIssue(parsed.error, [path])
  return { state: parsed.data.state }
}
export function parseInsightsRenderResponse(
  raw: unknown,
  scope: { shopDomain: string; orgId: string; connectionId: string },
): InsightsRenderResponse {
  const parsed = envelope.safeParse(raw)
  if (!parsed.success) throwRenderIssue(parsed.error)
  const data = parsed.data
  if (data.context)
    scopedContext(
      data.context,
      { shop: scope.shopDomain, orgId: scope.orgId, connectionId: scope.connectionId, revision: data.context.revision },
      'context',
    )
  if (data.state === 'unavailable') {
    if (data.context) {
      for (const key of insightAnswerKeys) {
        if (data.answers?.[key] !== undefined)
          metadataScope(data.answers[key], data.context, 'answers.unknown.evidence.context')
      }
    }
    return { state: 'unavailable' }
  }
  if (!data.context || !data.answers) throw new InsightsFailure('render_contract', 'missing_ready_fields', 'context')
  const issues: RenderIssue[] = []
  const answers: Record<string, RenderAnswer> = {}
  for (const key of insightAnswerKeys) {
    const rawAnswer = data.answers[key]
    if (rawAnswer === undefined) continue
    metadataScope(rawAnswer, data.context, 'answers.unknown.evidence.context', true)
    answers[key] = answer(rawAnswer, 'answers.unknown')
  }
  const composition = z.object({ page: z.unknown() }).safeParse(data.composition)
  if (!composition.success) throwRenderIssue(composition.error, ['composition'])
  const view = { page: parseRenderPage(composition.data.page, issues) }
  return { state: 'ready', context: data.context, answers, composition: view, renderIssues: issues }
}
